/**
 * Seeded terrace stacks: inset each shelf from the ring below it
 * and emit ordinary nested islands or rocks with independent jitter,
 * elevation, and inner-radius knobs.
 */

import {
  cloneRgba,
  hsvFromRgba,
  rgbaEquals,
  rgbaFromHsv,
  type Rgba,
} from '../color.ts';
import { polygonBounds, signedArea } from '../geometry.ts';
import { clamp, segmentIntersection, segmentsIntersect } from '../math.ts';
import { hashSeed, mulberry32, type Rng } from '../rng.ts';
import { clampElevation } from './document.ts';
import { ringContainsRing } from './elevationStack.ts';
import {
  addIsland,
  setIslandColor,
  setIslandCornerRadius,
  setIslandElevation,
} from './islands.ts';
import { DEFAULT_LAND_COLOR, DEFAULT_ROCK_COLOR } from './palette.ts';
import { createRing } from './ring.ts';
import { addRock, setRockColor, setRockCornerRadius, setRockElevation } from './rocks.ts';
import type { ObjectRef } from './selection.ts';
import {
  MIN_RING_VERTICES,
  type MapDocument,
  type TerraceKind,
  type Vec2,
} from './types.ts';

export const MIN_TERRACE_LEVELS = 1;
export const MAX_TERRACE_LEVELS = 20;
export const DEFAULT_TERRACE_LEVELS = 5;
export const DEFAULT_TERRACE_PEAK_M = 60;
export const DEFAULT_TERRACE_JITTER = 0.3;
export const DEFAULT_TERRACE_ELEV_RAND = 0.6;
export const DEFAULT_TERRACE_RADIUS_RAND = 0.45;
export const DEFAULT_TERRACE_COLOR_VARIATION = 0.4;
export const DEFAULT_TERRACE_RADIUS = 100;
export const MAX_TERRACE_RADIUS = 500;
export const DEFAULT_TERRACE_KIND: TerraceKind = 'island';
export const TERRACE_MAX_T = 0.62;

const INSET_ATTEMPTS = 8;
const MIN_INNER_AREA = 1e-3;
const MIN_WIDTH = 1e-3;
/** One step stays under half the narrowest corridor so both shores cannot meet. */
const WIDTH_FRACTION_CAP = 0.45;
/** A vertex may use most of its own clearance, not the whole ray to the far shore. */
const CLEARANCE_KEEP = 0.85;
/** Sharp corners slide at most this many steps along the bisector. */
const MITER_LIMIT = 4;
const HIT_EPSILON = 1e-3;
/** Peak HSV lift / sat drop at Color Variation 1. Factory 0.4 keeps yesterday’s light step. */
const COLOR_VALUE_LIFT = 0.55;
const COLOR_SAT_DROP = 0.55;

export type TerraceDraft = {
  kind: TerraceKind;
  levels: number;
  peakM: number;
  radius: number;
  color: Rgba;
  colorVariation: number;
  jitter: number;
  elevRand: number;
  radiusRand: number;
};

export type TerraceRingSpec = {
  vertices: Vec2[];
  cornerRadius: number;
};

export function clampTerraceLevels(value: number): number {
  return Math.round(clamp(value, MIN_TERRACE_LEVELS, MAX_TERRACE_LEVELS));
}

export function clampTerraceRandomness(value: number): number {
  return Math.round(clamp(value, 0, 1) * 20) / 20;
}

export function clampTerraceRadius(value: number): number {
  return clamp(value, 0, MAX_TERRACE_RADIUS);
}

export function defaultTerraceColor(kind: TerraceKind): Rgba {
  return cloneRgba(kind === 'rock' ? DEFAULT_ROCK_COLOR : DEFAULT_LAND_COLOR);
}

export function terraceColorForKind(kind: TerraceKind, current: Rgba): Rgba {
  if (rgbaEquals(current, DEFAULT_LAND_COLOR) || rgbaEquals(current, DEFAULT_ROCK_COLOR)) {
    return defaultTerraceColor(kind);
  }

  return cloneRgba(current);
}

export function terraceTint(
  base: Rgba,
  index: number,
  count: number,
  variation = DEFAULT_TERRACE_COLOR_VARIATION,
): Rgba {
  const amount = clampTerraceRandomness(variation);

  if (count <= 1 || index <= 0 || amount <= 0) {
    return cloneRgba(base);
  }

  const t = index / (count - 1);
  const hsv = hsvFromRgba(base);

  return rgbaFromHsv(
    {
      h: hsv.h,
      s: hsv.s * (1 - COLOR_SAT_DROP * amount * t),
      v: clamp(hsv.v + COLOR_VALUE_LIFT * amount * t, 0, 1),
    },
    base.a,
  );
}

export function terraceRisers(
  peakM: number,
  levels: number,
  elevRand = 0,
  rng?: Rng,
): number[] {
  const n = clampTerraceLevels(levels);
  const peak = clampElevation(peakM);

  if (n <= 1) {
    return [peak];
  }

  const even = peak / n;
  const rand = clampTerraceRandomness(elevRand);

  if (rand <= 0 || !rng) {
    const elevations: number[] = [];

    for (let i = 0; i < n - 1; i += 1) {
      elevations.push(even);
    }

    elevations.push(clampElevation(peak - even * (n - 1)));
    return elevations;
  }

  const floor = Math.max(Math.min(1, even), even * (1 - 0.7 * rand));
  const weights: number[] = [];

  for (let i = 0; i < n; i += 1) {
    weights.push(Math.max(floor, even * (1 + (rng() * 2 - 1) * rand)));
  }

  const sum = weights.reduce((total, weight) => total + weight, 0);
  const scaled = weights.map((weight) => (weight / sum) * peak);
  const used = scaled.slice(0, -1).reduce((total, value) => total + value, 0);
  scaled[n - 1] = clampElevation(peak - used);
  return scaled;
}

function cloneVertices(vertices: readonly Vec2[]): Vec2[] {
  return vertices.map((vertex) => ({ x: vertex.x, y: vertex.y }));
}

function fingerprint(vertices: readonly Vec2[]): string {
  return vertices.map((vertex) => `${vertex.x.toFixed(3)},${vertex.y.toFixed(3)}`).join(';');
}

function unit(x: number, y: number): Vec2 | null {
  const length = Math.hypot(x, y);

  if (length < 1e-8) {
    return null;
  }

  return { x: x / length, y: y / length };
}

function edgeNormal(a: Vec2, b: Vec2, interiorLeft: boolean): Vec2 | null {
  const dx = b.x - a.x;
  const dy = b.y - a.y;

  return interiorLeft ? unit(-dy, dx) : unit(dy, -dx);
}

function reachOf(vertices: readonly Vec2[]): number {
  const box = polygonBounds(vertices);

  if (!box) {
    return 0;
  }

  return Math.hypot(box.maxX - box.minX, box.maxY - box.minY);
}

function nearestInwardHit(
  origin: Vec2,
  direction: Vec2,
  vertices: readonly Vec2[],
  skip: (edge: number) => boolean,
  reach: number,
): number | null {
  const end = {
    x: origin.x + direction.x * reach,
    y: origin.y + direction.y * reach,
  };
  let best = Number.POSITIVE_INFINITY;

  for (let edge = 0; edge < vertices.length; edge += 1) {
    if (skip(edge)) {
      continue;
    }

    const a = vertices[edge];
    const b = vertices[(edge + 1) % vertices.length];

    if (!a || !b) {
      continue;
    }

    const hit = segmentIntersection(origin, end, a, b);

    if (!hit) {
      continue;
    }

    const distance = Math.hypot(hit.x - origin.x, hit.y - origin.y);

    if (distance > HIT_EPSILON && distance < best) {
      best = distance;
    }
  }

  return Number.isFinite(best) ? best : null;
}

function minInwardWidth(vertices: readonly Vec2[]): number | null {
  const reach = reachOf(vertices);

  if (reach < MIN_WIDTH) {
    return null;
  }

  const interiorLeft = signedArea(vertices) >= 0;
  let best = Number.POSITIVE_INFINITY;

  for (let edge = 0; edge < vertices.length; edge += 1) {
    const a = vertices[edge];
    const b = vertices[(edge + 1) % vertices.length];

    if (!a || !b) {
      continue;
    }

    const normal = edgeNormal(a, b, interiorLeft);

    if (!normal) {
      continue;
    }

    const hit = nearestInwardHit(
      { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      normal,
      vertices,
      (index) => index === edge,
      reach,
    );

    if (hit != null && hit < best) {
      best = hit;
    }
  }

  return Number.isFinite(best) ? best : null;
}

function edgesCross(vertices: readonly Vec2[]): boolean {
  const count = vertices.length;

  for (let i = 0; i < count; i += 1) {
    const a1 = vertices[i];
    const a2 = vertices[(i + 1) % count];

    if (!a1 || !a2) {
      continue;
    }

    for (let j = i + 1; j < count; j += 1) {
      if (j === i + 1 || (i === 0 && j === count - 1)) {
        continue;
      }

      const b1 = vertices[j];
      const b2 = vertices[(j + 1) % count];

      if (!b1 || !b2) {
        continue;
      }

      if (segmentsIntersect(a1, a2, b1, b2)) {
        return true;
      }
    }
  }

  return false;
}

function insetRing(
  vertices: readonly Vec2[],
  distance: number,
  jitter: number,
  rng: Rng,
): Vec2[] | null {
  if (distance <= HIT_EPSILON || vertices.length < MIN_RING_VERTICES) {
    return null;
  }

  const interiorLeft = signedArea(vertices) >= 0;
  const reach = reachOf(vertices) + distance;
  const count = vertices.length;
  const next: Vec2[] = [];

  for (let i = 0; i < count; i += 1) {
    const prev = vertices[(i - 1 + count) % count];
    const vertex = vertices[i];
    const following = vertices[(i + 1) % count];

    if (!prev || !vertex || !following) {
      return null;
    }

    const normalIn = edgeNormal(prev, vertex, interiorLeft);
    const normalOut = edgeNormal(vertex, following, interiorLeft);

    if (!normalIn || !normalOut) {
      return null;
    }

    const scale = jitter <= 0 ? 1 : clamp(1 + (rng() * 2 - 1) * jitter, 0.45, 1);
    const step = distance * scale;
    const bisector = unit(normalIn.x + normalOut.x, normalIn.y + normalOut.y) ?? normalIn;
    const denom = bisector.x * normalIn.x + bisector.y * normalIn.y;
    let miter = denom > 1e-4 ? step / denom : step;
    miter = Math.min(miter, MITER_LIMIT * step);

    const clearance = nearestInwardHit(
      vertex,
      bisector,
      vertices,
      (edge) => edge === (i - 1 + count) % count || edge === i,
      reach,
    );

    if (clearance != null) {
      miter = Math.min(miter, clearance * CLEARANCE_KEEP);
    }

    next.push({
      x: vertex.x + bisector.x * miter,
      y: vertex.y + bisector.y * miter,
    });
  }

  const area = signedArea(next);

  if (area * signedArea(vertices) <= 0 || Math.abs(area) < MIN_INNER_AREA || edgesCross(next)) {
    return null;
  }

  return next;
}

export function buildTerraceRings(
  outer: readonly Vec2[],
  options: {
    levels: number;
    jitter: number;
    radiusRand?: number;
    seed: number;
    cornerRadius?: number;
  },
): TerraceRingSpec[] {
  const levels = clampTerraceLevels(options.levels);
  const jitter = clampTerraceRandomness(options.jitter);
  const radiusRand = clampTerraceRandomness(options.radiusRand ?? 0);
  const baseRadius = clampTerraceRadius(options.cornerRadius ?? DEFAULT_TERRACE_RADIUS);
  const jitterRng = mulberry32(hashSeed(options.seed, 'jitter'));
  const radiusRng = mulberry32(hashSeed(options.seed, 'radius'));
  const rings: TerraceRingSpec[] = [{ vertices: cloneVertices(outer), cornerRadius: baseRadius }];

  if (levels <= 1 || outer.length < MIN_RING_VERTICES) {
    return rings;
  }

  for (let k = 1; k < levels; k += 1) {
    const t = (k / levels) * TERRACE_MAX_T;
    const planned = Math.max(0, baseRadius * (1 - t));
    const previous = rings[rings.length - 1];

    if (!previous) {
      break;
    }

    const width = minInwardWidth(previous.vertices);

    if (width == null || width < MIN_WIDTH) {
      break;
    }

    const step = width * Math.min(TERRACE_MAX_T / levels, WIDTH_FRACTION_CAP);
    const parent = createRing(previous.vertices, previous.cornerRadius);
    let accepted: TerraceRingSpec | null = null;

    for (let attempt = 0; attempt < INSET_ATTEMPTS; attempt += 1) {
      const scale = 1 - attempt / INSET_ATTEMPTS;
      const vertices = insetRing(previous.vertices, step * scale, jitter, jitterRng);

      if (!vertices) {
        continue;
      }

      const radius =
        radiusRand <= 0
          ? planned
          : Math.max(0, planned * (1 + (radiusRng() * 2 - 1) * radiusRand * 0.4 * scale));
      const candidate = createRing(vertices, radius);

      if (ringContainsRing(parent, candidate)) {
        accepted = { vertices, cornerRadius: radius };
        break;
      }
    }

    if (!accepted) {
      break;
    }

    rings.push(accepted);
  }

  return rings;
}

export function placeTerraces(
  map: MapDocument,
  vertices: readonly Vec2[],
  draft: TerraceDraft,
): ObjectRef[] {
  if (vertices.length < MIN_RING_VERTICES) {
    return [];
  }

  const seed = hashSeed(map.environment.seed, 'terrace', fingerprint(vertices));
  const rings = buildTerraceRings(vertices, {
    levels: draft.levels,
    jitter: draft.jitter,
    radiusRand: draft.radiusRand,
    seed,
    cornerRadius: draft.radius,
  });
  const elevations = terraceRisers(
    draft.peakM,
    rings.length,
    draft.elevRand,
    mulberry32(hashSeed(seed, 'elev')),
  );
  const refs: ObjectRef[] = [];

  for (let i = 0; i < rings.length; i += 1) {
    const ring = rings[i];
    const elevation = elevations[i] ?? 0;

    if (!ring) {
      continue;
    }

    const color = terraceTint(draft.color, i, rings.length, draft.colorVariation);

    if (draft.kind === 'rock') {
      const rock = addRock(map, ring.vertices);
      setRockCornerRadius(map, rock.id, ring.cornerRadius);
      setRockElevation(map, rock.id, elevation);
      setRockColor(map, rock.id, color);
      refs.push({ kind: 'rock', id: rock.id });
    } else {
      const island = addIsland(map, ring.vertices);
      setIslandCornerRadius(map, island.id, ring.cornerRadius);
      setIslandElevation(map, island.id, elevation);
      setIslandColor(map, island.id, color);
      refs.push({ kind: 'island', id: island.id });
    }
  }

  return refs;
}
